import { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ownerNav } from "@/config/nav";
import api, { formatINR, API_BASE } from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Receipt, Bank, Printer, DownloadSimple } from "@phosphor-icons/react";

const downloadStatementPdf = () => {
  const token = localStorage.getItem("avb_token");
  fetch(`${API_BASE}/owner/statement/pdf`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = url; a.download = "avinya_statement.pdf"; a.click();
      URL.revokeObjectURL(url);
    });
};

const whatsappStatement = () => api.get("/owner/statement/link").then((r) => {
  if (r.data.link) window.open(`https://wa.me/?text=${encodeURIComponent(`My Avinya Bookings statement: ${r.data.link}`)}`, "_blank");
  else toast.error("Statement link not available.");
}).catch(() => toast.error("Couldn't fetch statement link"));

const STATUS_COLORS = {
  PENDING: "bg-stone-100 text-stone-600", ELIGIBLE: "bg-blue-100 text-blue-700",
  PROCESSING: "bg-amber-100 text-amber-700", PAID: "bg-green-100 text-green-700",
  ON_HOLD: "bg-orange-100 text-orange-700", FAILED: "bg-red-100 text-red-700",
};

export default function OwnerStatement() {
  const [d, setD] = useState(null);
  const [pd, setPd] = useState(null);
  const [form, setForm] = useState({ account_holder: "", account_number: "", ifsc: "", upi_id: "" });

  const load = () => {
    api.get("/owner/statement").then((r) => setD(r.data)).catch(() => toast.error("Couldn't load statement"));
    api.get("/owner/payout-details").then((r) => { setPd(r.data); setForm((f) => ({ ...f, account_holder: r.data.account_holder, ifsc: r.data.ifsc, upi_id: r.data.upi_id })); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const savePd = () => {
    api.put("/owner/payout-details", form).then(() => { toast.success("Payout details saved securely"); load(); }).catch(() => toast.error("Save failed"));
  };

  if (!d) return <DashboardLayout nav={ownerNav} title="Owner"><div className="flex justify-center py-24"><Spinner /></div></DashboardLayout>;

  return (
    <DashboardLayout nav={ownerNav} title="Owner">
      <div className="flex items-center gap-2">
        <Receipt size={26} className="text-primary" />
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Financial Statement</h1>
        <Button data-testid="statement-pdf" variant="outline" size="sm" className="ml-auto gap-1.5 rounded-full" onClick={downloadStatementPdf}><DownloadSimple size={16} /> Download PDF</Button>
        <Button data-testid="statement-whatsapp" variant="outline" size="sm" className="gap-1.5 rounded-full text-green-700" onClick={whatsappStatement}><Bank size={16} /> WhatsApp</Button>
        <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => window.print()} data-testid="statement-print"><Printer size={16} /> Print</Button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="statement-summary">
        <div className="rounded-2xl border border-stone-200 bg-white p-4"><p className="text-xs text-stone-400">Available for payout</p><p className="mt-1 font-heading text-xl font-bold text-blue-700">{formatINR(d.available)}</p></div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4"><p className="text-xs text-stone-400">Pending</p><p className="mt-1 font-heading text-xl font-bold text-stone-700">{formatINR(d.pending)}</p></div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4"><p className="text-xs text-stone-400">Paid out</p><p className="mt-1 font-heading text-xl font-bold text-green-600">{formatINR(d.paid)}</p></div>
        <div className="rounded-2xl border border-stone-200 bg-white p-4"><p className="text-xs text-stone-400">Total booking value</p><p className="mt-1 font-heading text-xl font-bold text-stone-900">{formatINR(d.total_booking_value)}</p></div>
      </div>

      {/* Payout details */}
      <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-5">
        <p className="flex items-center gap-2 font-heading font-semibold text-stone-900"><Bank size={18} className="text-primary" /> Payout Details</p>
        <p className="mt-1 text-xs text-stone-400">Stored securely. {pd?.has_details ? `Current account: ${pd.account_number_masked || "—"}` : "No payout method saved yet."}</p>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div><label className="text-xs text-stone-500">Account holder name</label><Input data-testid="pd-holder" value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} /></div>
          <div><label className="text-xs text-stone-500">Bank account number</label><Input data-testid="pd-account" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder={pd?.account_number_masked || "Enter to update"} /></div>
          <div><label className="text-xs text-stone-500">IFSC</label><Input data-testid="pd-ifsc" value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} /></div>
          <div><label className="text-xs text-stone-500">UPI ID</label><Input data-testid="pd-upi" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} /></div>
        </div>
        <Button data-testid="pd-save" className="mt-4 rounded-full" onClick={savePd}>Save payout details</Button>
      </div>

      {/* Statement rows */}
      <div className="mt-8 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        <table className="w-full text-sm" data-testid="statement-table">
          <thead className="bg-stone-50 text-left text-xs text-stone-500"><tr>
            <th className="p-3">Booking</th><th className="p-3">Property</th><th className="p-3">Event date</th>
            <th className="p-3 text-right">Booking value</th><th className="p-3 text-right">Commission</th>
            <th className="p-3 text-right">Refund</th><th className="p-3 text-right">Net</th><th className="p-3">Payout</th>
          </tr></thead>
          <tbody>
            {d.rows.length === 0 ? <tr><td colSpan={8} className="p-6 text-center text-stone-400">No paid bookings yet.</td></tr> :
              d.rows.map((r) => (
                <tr key={r.booking_ref} className="border-t border-stone-100">
                  <td className="p-3 font-medium text-stone-800">{r.booking_ref}</td>
                  <td className="p-3 text-stone-600">{r.property}</td>
                  <td className="p-3 text-stone-500">{r.event_date}</td>
                  <td className="p-3 text-right">{formatINR(r.booking_value)}</td>
                  <td className="p-3 text-right text-primary">{formatINR(r.commission)}</td>
                  <td className="p-3 text-right text-red-600">{r.refund ? formatINR(r.refund) : "—"}</td>
                  <td className="p-3 text-right font-semibold">{formatINR(r.net_amount)}</td>
                  <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[r.payout_status] || "bg-stone-100"}`}>{r.payout_status}</span></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
